<script setup lang="ts">
/**
 * WhatsApp-style audio. A voice note: the sender's picture with a microphone (green until played,
 * blue after), a solid play button and the note's own waveform, silence as dots; while it plays
 * the picture gives way to the speed pill. An audio file shows a headphones tile instead.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import Icon from "./Icon.vue";
import { barHeight, voiceLevels } from "../composables/waveform";
import { locale, t } from "../i18n";

const props = defineProps<{
  src?: string | undefined;
  /** Stable seed for the bar heights (the message id). */
  seed: string;
  voice?: boolean;
  durationMs?: number | null | undefined;
  name?: string | undefined;
  /** Initials shown on the voice-note avatar. */
  initials?: string | undefined;
  out?: boolean;
  /** The recipient played this voice note (WhatsApp turns the microphone blue). */
  played?: boolean;
  /** Delivered but not played yet: WhatsApp's microphone is green then, grey before. */
  delivered?: boolean;
}>();

/** Measured on WhatsApp Web (2026-09-25): 44 bars of 2.75 px in 200 px, 5 to 22 px tall. */
const BARS = 44;
const BAR_MAX = 22;
const RATES = [1, 1.5, 2] as const;
const player = ref<HTMLAudioElement>();
const playing = ref(false);
const position = ref(0);
const total = ref((props.durationMs ?? 0) / 1000);
const rate = ref<(typeof RATES)[number]>(1);
const failed = ref(false);

const root = ref<HTMLElement>();
/** The note's own levels, read from the file once it is on screen. */
const levels = ref<number[] | null>(null);
/** Until the file is read (or when it cannot be), a steady made-up shape. */
const heights = computed<Array<number | null>>(() => {
  if (levels.value) return levels.value.map((l) => barHeight(l, BAR_MAX));
  let h = 2166136261;
  for (const ch of props.seed) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return Array.from({ length: BARS }, (_, i) => {
    h = Math.imul(h ^ (i + 1), 16777619);
    return 4 + (Math.abs(h) % (BAR_MAX - 3));
  });
});
let watcher: IntersectionObserver | undefined;
onMounted(() => {
  if (!props.voice || !props.src || !root.value) return;
  if (typeof IntersectionObserver === "undefined") return;
  watcher = new IntersectionObserver((seen) => {
    if (!seen.some((e) => e.isIntersecting) || !props.src) return;
    watcher?.disconnect();
    void voiceLevels(props.src, BARS).then((l) => (levels.value = l));
  });
  watcher.observe(root.value);
});
onBeforeUnmount(() => watcher?.disconnect());
const progress = computed(() =>
  total.value ? Math.min(1, position.value / total.value) : 0,
);
const clock = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const label = computed(() =>
  clock(playing.value || position.value ? position.value : total.value),
);
/** Once playing starts the speed pill takes the picture's place, as on WhatsApp. */
const started = computed(() => playing.value || position.value > 0);
/** "1,0×", "1,5×", "2,0×", as WhatsApp writes it. */
const rateLabel = computed(() =>
  rate.value.toLocaleString(locale.value, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }),
);
/** Played here, for a received note (for a sent one only the recipient's play counts). */
const playedHere = ref(false);
/**
 * WhatsApp's colours: a received note is green (knob and microphone) until heard, then blue; a
 * sent note has a blue knob and a microphone that is grey when sent, green when delivered and
 * blue once the recipient played it.
 */
const heard = computed(() => props.played || (!props.out && playedHere.value));
const micTone = computed(() =>
  heard.value
    ? "heard"
    : props.out
      ? props.delivered
        ? "delivered"
        : "sent"
      : "new",
);

function toggle() {
  const el = player.value;
  if (!el || failed.value) return;
  if (playing.value) el.pause();
  else {
    el.playbackRate = rate.value;
    void el.play().catch(() => {
      playing.value = false;
      failed.value = true;
    });
  }
}
function cycleRate() {
  rate.value = RATES[(RATES.indexOf(rate.value) + 1) % RATES.length]!;
  if (player.value) player.value.playbackRate = rate.value;
}
function seek(e: MouseEvent) {
  const el = player.value;
  const bar = e.currentTarget as HTMLElement;
  if (!el || !total.value) return;
  const r = bar.getBoundingClientRect();
  el.currentTime =
    Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * total.value;
  position.value = el.currentTime;
}
function seekKey(e: KeyboardEvent) {
  const el = player.value;
  if (!el || !total.value) return;
  if (e.key === "ArrowRight")
    el.currentTime = Math.min(total.value, el.currentTime + 5);
  else if (e.key === "ArrowLeft")
    el.currentTime = Math.max(0, el.currentTime - 5);
  else return;
  e.preventDefault();
  position.value = el.currentTime;
}
function loaded() {
  const d = player.value?.duration;
  if (d && Number.isFinite(d)) total.value = d;
}
onBeforeUnmount(() => player.value?.pause());
</script>

<template>
  <div
    ref="root"
    class="audio-player"
    :class="{ voice, playing, out, started, heard }"
  >
    <!-- The picture's place: the picture with its microphone, or the speed while it plays. -->
    <span v-if="voice" class="voice-slot">
      <button
        v-if="started"
        type="button"
        class="audio-rate"
        :aria-label="t('media.audio.rate')"
        @click="cycleRate"
      >
        {{ rateLabel }}×
      </button>
      <span v-else class="voice-avatar"
        ><span class="voice-initials">{{ initials || "" }}</span></span
      >
      <!-- WhatsApp's own "ptt-status" microphone, outlined in the bubble's colour. -->
      <svg
        v-if="!started"
        class="voice-mic"
        :class="micTone"
        viewBox="0 0 19 26"
        width="19"
        height="26"
        :aria-label="heard ? t('delivery.played') : undefined"
      >
        <path
          class="voice-mic-outline"
          d="M9.217,24.401c-1.158,0-2.1-0.941-2.1-2.1v-2.366c-2.646-0.848-4.652-3.146-5.061-5.958L2.004,13.62 l-0.003-0.081c-0.021-0.559,0.182-1.088,0.571-1.492c0.39-0.404,0.939-0.637,1.507-0.637h0.3c0.254,0,0.498,0.044,0.724,0.125v-6.27 C5.103,2.913,7.016,1,9.367,1c2.352,0,4.265,1.913,4.265,4.265v6.271c0.226-0.081,0.469-0.125,0.723-0.125h0.3 c0.564,0,1.112,0.233,1.501,0.64s0.597,0.963,0.571,1.526c0,0.005,0.001,0.124-0.08,0.6c-0.47,2.703-2.459,4.917-5.029,5.748v2.378 c0,1.158-0.942,2.1-2.1,2.1H9.217V24.401z"
        />
        <path
          fill="currentColor"
          d="M9.367,15.668c1.527,0,2.765-1.238,2.765-2.765V5.265c0-1.527-1.238-2.765-2.765-2.765 S6.603,3.738,6.603,5.265v7.638C6.603,14.43,7.84,15.668,9.367,15.668z M14.655,12.91h-0.3c-0.33,0-0.614,0.269-0.631,0.598 c0,0,0,0-0.059,0.285c-0.41,1.997-2.182,3.505-4.298,3.505c-2.126,0-3.904-1.521-4.304-3.531C5.008,13.49,5.008,13.49,5.008,13.49 c-0.016-0.319-0.299-0.579-0.629-0.579h-0.3c-0.33,0-0.591,0.258-0.579,0.573c0,0,0,0,0.04,0.278 c0.378,2.599,2.464,4.643,5.076,4.978v3.562c0,0.33,0.27,0.6,0.6,0.6h0.3c0.33,0,0.6-0.27,0.6-0.6V18.73 c2.557-0.33,4.613-2.286,5.051-4.809c0.057-0.328,0.061-0.411,0.061-0.411C15.243,13.18,14.985,12.91,14.655,12.91z"
        />
      </svg>
    </span>
    <span v-else class="audio-file-tile"><Icon name="audio" :size="24" /></span>
    <button
      type="button"
      class="audio-toggle"
      :aria-label="playing ? t('media.audio.pause') : t('media.audio.play')"
      :disabled="!src || failed"
      @click="toggle"
    >
      <!-- WhatsApp Web's ic-play-arrow-filled and ic-pause-filled. -->
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path
          v-if="playing"
          fill="currentColor"
          d="M16 19c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V7c0-.55.2-1.02.59-1.41.39-.4.86-.59 1.41-.59.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41v10c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59Zm-8 0c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41V7c0-.55.2-1.02.59-1.41C6.98 5.19 7.45 5 8 5c.55 0 1.02.2 1.41.59.4.39.59.86.59 1.41v10c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59Z"
        />
        <path
          v-else
          fill="currentColor"
          d="M9.53 18.02a.91.91 0 0 1-1.02.04.95.95 0 0 1-.51-.88V6.82c0-.4.17-.7.51-.88a.91.91 0 0 1 1.02.03l8.15 5.18c.3.2.45.48.45.85s-.15.65-.45.85l-8.15 5.17Z"
        />
      </svg>
    </button>
    <div class="audio-body grow">
      <div
        class="audio-wave"
        role="slider"
        tabindex="0"
        :aria-label="t('media.audio.position')"
        :aria-valuemin="0"
        :aria-valuemax="Math.round(total)"
        :aria-valuenow="Math.round(position)"
        @click="seek"
        @keydown="seekKey"
      >
        <i
          v-for="(h, i) in heights"
          :key="i"
          :class="{
            played: (i + 0.5) / heights.length <= progress,
            dot: h === null,
          }"
          :style="h === null ? undefined : { height: h + 'px' }"
        />
        <span class="audio-knob" :style="{ left: progress * 100 + '%' }" />
      </div>
      <span class="audio-meta small"
        ><span>{{ label }}</span
        ><span v-if="name && !voice" class="truncate">{{ name }}</span></span
      >
    </div>
    <audio
      ref="player"
      :src="src"
      preload="metadata"
      @loadedmetadata="loaded"
      @durationchange="loaded"
      @timeupdate="position = player?.currentTime ?? 0"
      @play="
        playing = true;
        playedHere = true;
      "
      @pause="playing = false"
      @ended="
        playing = false;
        position = 0;
      "
      @error="failed = true"
    />
  </div>
</template>
